/**
 * Firestore Service Layer
 *
 * Provides CRUD operations for students, word banks, and words.
 * Data is scoped to the authenticated teacher via their Firebase UID.
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment,
  onSnapshot,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './config';
import type { AnimalType } from '@/components/AnimalPassword';

// ============================================================================
// Types
// ============================================================================

export interface FirestoreStudent {
  id: string;
  teacherId: string;
  displayName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface FirestoreWordBank {
  id: string;
  teacherId: string;
  name: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface FirestoreWord {
  id: string;
  wordBankId: string;
  text: string;
  phonemeCount: number;
  syllableTypes: string[];
  expectedSegmentation: string[];
  tags: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================================================
// Students
// ============================================================================

const studentsCollection = collection(db, 'students');

export const studentsService = {
  async getAll(teacherId: string): Promise<FirestoreStudent[]> {
    const q = query(
      studentsCollection,
      where('teacherId', '==', teacherId),
      orderBy('displayName', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as FirestoreStudent[];
  },

  async create(teacherId: string, displayName: string): Promise<FirestoreStudent> {
    const docRef = await addDoc(studentsCollection, {
      teacherId,
      displayName: displayName.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const newDoc = await getDoc(docRef);
    return { id: newDoc.id, ...newDoc.data() } as FirestoreStudent;
  },

  async update(id: string, teacherId: string, displayName: string): Promise<FirestoreStudent> {
    const docRef = doc(db, 'students', id);
    const existingDoc = await getDoc(docRef);

    if (!existingDoc.exists()) {
      throw new Error('Student not found');
    }

    if (existingDoc.data()?.teacherId !== teacherId) {
      throw new Error('Permission denied');
    }

    await updateDoc(docRef, {
      displayName: displayName.trim(),
      updatedAt: serverTimestamp(),
    });

    const updatedDoc = await getDoc(docRef);
    return { id: updatedDoc.id, ...updatedDoc.data() } as FirestoreStudent;
  },

  async delete(id: string, teacherId: string): Promise<void> {
    const docRef = doc(db, 'students', id);
    const existingDoc = await getDoc(docRef);

    if (!existingDoc.exists()) {
      throw new Error('Student not found');
    }

    if (existingDoc.data()?.teacherId !== teacherId) {
      throw new Error('Permission denied');
    }

    await deleteDoc(docRef);
  },
};

// ============================================================================
// Word Banks
// ============================================================================

const wordBanksCollection = collection(db, 'wordBanks');

export const wordBanksService = {
  async getAll(teacherId: string): Promise<FirestoreWordBank[]> {
    const q = query(
      wordBanksCollection,
      where('teacherId', '==', teacherId),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as FirestoreWordBank[];
  },

  async getById(id: string, teacherId: string): Promise<FirestoreWordBank & { words: FirestoreWord[] }> {
    const docRef = doc(db, 'wordBanks', id);
    const wordBankDoc = await getDoc(docRef);

    if (!wordBankDoc.exists()) {
      throw new Error('Word bank not found');
    }

    if (wordBankDoc.data()?.teacherId !== teacherId) {
      throw new Error('Permission denied');
    }

    // Get words for this word bank
    const words = await wordsService.getByWordBankId(id);

    return {
      id: wordBankDoc.id,
      ...wordBankDoc.data(),
      words,
    } as FirestoreWordBank & { words: FirestoreWord[] };
  },

  async create(teacherId: string, name: string): Promise<FirestoreWordBank> {
    const docRef = await addDoc(wordBanksCollection, {
      teacherId,
      name: name.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const newDoc = await getDoc(docRef);
    return { id: newDoc.id, ...newDoc.data() } as FirestoreWordBank;
  },

  async update(id: string, teacherId: string, name: string): Promise<FirestoreWordBank> {
    const docRef = doc(db, 'wordBanks', id);
    const existingDoc = await getDoc(docRef);

    if (!existingDoc.exists()) {
      throw new Error('Word bank not found');
    }

    if (existingDoc.data()?.teacherId !== teacherId) {
      throw new Error('Permission denied');
    }

    await updateDoc(docRef, {
      name: name.trim(),
      updatedAt: serverTimestamp(),
    });

    const updatedDoc = await getDoc(docRef);
    return { id: updatedDoc.id, ...updatedDoc.data() } as FirestoreWordBank;
  },

  async delete(id: string, teacherId: string): Promise<void> {
    const docRef = doc(db, 'wordBanks', id);
    const existingDoc = await getDoc(docRef);

    if (!existingDoc.exists()) {
      throw new Error('Word bank not found');
    }

    if (existingDoc.data()?.teacherId !== teacherId) {
      throw new Error('Permission denied');
    }

    // Delete all words in this word bank first
    const words = await wordsService.getByWordBankId(id);
    for (const word of words) {
      await deleteDoc(doc(db, 'words', word.id));
    }

    await deleteDoc(docRef);
  },
};

// ============================================================================
// Words
// ============================================================================

const wordsCollection = collection(db, 'words');

export const wordsService = {
  async getByWordBankId(wordBankId: string): Promise<FirestoreWord[]> {
    const q = query(
      wordsCollection,
      where('wordBankId', '==', wordBankId),
      orderBy('text', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as FirestoreWord[];
  },

  async create(
    wordBankId: string,
    wordData: {
      text: string;
      phonemeCount?: number;
      syllableTypes?: string[];
      expectedSegmentation?: string[];
      tags?: string[];
    }
  ): Promise<FirestoreWord> {
    const docRef = await addDoc(wordsCollection, {
      wordBankId,
      text: wordData.text.trim(),
      phonemeCount: wordData.phonemeCount || 1,
      syllableTypes: wordData.syllableTypes || [],
      expectedSegmentation: wordData.expectedSegmentation || [],
      tags: wordData.tags || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const newDoc = await getDoc(docRef);
    return { id: newDoc.id, ...newDoc.data() } as FirestoreWord;
  },

  async createMany(
    wordBankId: string,
    words: Array<{
      text: string;
      phonemeCount?: number;
      syllableTypes?: string[];
      expectedSegmentation?: string[];
      tags?: string[];
    }>
  ): Promise<FirestoreWord[]> {
    const created: FirestoreWord[] = [];
    for (const wordData of words) {
      const word = await this.create(wordBankId, wordData);
      created.push(word);
    }
    return created;
  },

  async update(
    id: string,
    wordData: {
      text?: string;
      phonemeCount?: number;
      syllableTypes?: string[];
      expectedSegmentation?: string[];
      tags?: string[];
    }
  ): Promise<FirestoreWord> {
    const docRef = doc(db, 'words', id);
    const existingDoc = await getDoc(docRef);

    if (!existingDoc.exists()) {
      throw new Error('Word not found');
    }

    const updates: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
    };

    if (wordData.text !== undefined) updates.text = wordData.text.trim();
    if (wordData.phonemeCount !== undefined) updates.phonemeCount = wordData.phonemeCount;
    if (wordData.syllableTypes !== undefined) updates.syllableTypes = wordData.syllableTypes;
    if (wordData.expectedSegmentation !== undefined) updates.expectedSegmentation = wordData.expectedSegmentation;
    if (wordData.tags !== undefined) updates.tags = wordData.tags;

    await updateDoc(docRef, updates);

    const updatedDoc = await getDoc(docRef);
    return { id: updatedDoc.id, ...updatedDoc.data() } as FirestoreWord;
  },

  async delete(id: string): Promise<void> {
    const docRef = doc(db, 'words', id);
    await deleteDoc(docRef);
  },
};

// ============================================================================
// Configurations (Teacher Settings)
// ============================================================================

const configurationsCollection = collection(db, 'configurations');

export const configurationsService = {
  async get(teacherId: string): Promise<Record<string, Record<string, unknown>>> {
    const q = query(
      configurationsCollection,
      where('teacherId', '==', teacherId)
    );
    const snapshot = await getDocs(q);

    const configs: Record<string, Record<string, unknown>> = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.type) {
        configs[data.type] = data.config || {};
      }
    });

    return configs;
  },

  async save(
    teacherId: string,
    type: string,
    config: Record<string, unknown>
  ): Promise<void> {
    // Check if config exists for this teacher + type
    const q = query(
      configurationsCollection,
      where('teacherId', '==', teacherId),
      where('type', '==', type)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Create new
      await addDoc(configurationsCollection, {
        teacherId,
        type,
        config,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Update existing
      const docRef = snapshot.docs[0].ref;
      await updateDoc(docRef, {
        config,
        updatedAt: serverTimestamp(),
      });
    }
  },
};

// ============================================================================
// Sessions (Animal Password System)
// ============================================================================

export interface FirestoreTeacherSession {
  teacherId: string;
  animalCode: AnimalType[];
  lastLogin: Timestamp;
  deviceCount: number;
  maxDevices: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  /** Cast state - which app is being broadcast to students */
  cast?: {
    appId: string;
    startedAt: Timestamp;
    teacherDeviceId: string;
  } | null;
}

export interface FirestoreDevice {
  id: string;
  sessionId: string;
  deviceId: string;
  userType: 'teacher' | 'student';
  lastActive: Timestamp;
  createdAt: Timestamp;
}

/**
 * Sessions service for managing teacher sessions and student device tracking.
 * Each teacher has one active session document that stores their animal code.
 * Devices are tracked in a subcollection.
 */
export const sessionsService = {
  /**
   * Get or create a teacher session.
   * If the session doesn't exist or is older than 24 hours, generates a new animal code.
   */
  async getOrCreateSession(
    teacherId: string,
    generateAnimalCode: () => AnimalType[]
  ): Promise<{ animalCode: AnimalType[]; deviceCount: number; isNew: boolean }> {
    const sessionRef = doc(db, 'sessions', teacherId);
    const sessionDoc = await getDoc(sessionRef);

    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;

    if (sessionDoc.exists()) {
      const data = sessionDoc.data() as FirestoreTeacherSession;
      const lastLoginTime = data.lastLogin?.toMillis() || 0;

      // Check if session is still valid (less than 24 hours old)
      if (now - lastLoginTime < twentyFourHours) {
        // Update last login time
        await updateDoc(sessionRef, {
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        return {
          animalCode: data.animalCode,
          deviceCount: data.deviceCount || 0,
          isNew: false,
        };
      }
    }

    // Create new session with fresh animal code
    const newCode = generateAnimalCode();
    await setDoc(sessionRef, {
      teacherId,
      animalCode: newCode,
      lastLogin: serverTimestamp(),
      deviceCount: 0,
      maxDevices: 30,
      createdAt: sessionDoc.exists() ? sessionDoc.data()?.createdAt : serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Clear old devices when creating new session
    await this.clearAllDevices(teacherId);

    return {
      animalCode: newCode,
      deviceCount: 0,
      isNew: true,
    };
  },

  /**
   * Get a teacher's current animal code (for student validation).
   * Returns null if no valid session exists.
   */
  async getAnimalCode(teacherId: string): Promise<AnimalType[] | null> {
    const sessionRef = doc(db, 'sessions', teacherId);
    const sessionDoc = await getDoc(sessionRef);

    if (!sessionDoc.exists()) {
      return null;
    }

    const data = sessionDoc.data() as FirestoreTeacherSession;
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    const lastLoginTime = data.lastLogin?.toMillis() || 0;

    // Check if session is still valid
    if (now - lastLoginTime >= twentyFourHours) {
      return null;
    }

    return data.animalCode;
  },

  /**
   * Generate and save a new animal code for a teacher.
   */
  async regenerateAnimalCode(
    teacherId: string,
    generateAnimalCode: () => AnimalType[]
  ): Promise<AnimalType[]> {
    const sessionRef = doc(db, 'sessions', teacherId);
    const newCode = generateAnimalCode();

    await updateDoc(sessionRef, {
      animalCode: newCode,
      updatedAt: serverTimestamp(),
    });

    return newCode;
  },

  /**
   * Register a device (teacher or student).
   * Returns false if device limit is reached.
   */
  async registerDevice(
    teacherId: string,
    deviceId: string,
    userType: 'teacher' | 'student'
  ): Promise<{ success: boolean; deviceCount: number }> {
    const sessionRef = doc(db, 'sessions', teacherId);
    const sessionDoc = await getDoc(sessionRef);

    if (!sessionDoc.exists()) {
      return { success: false, deviceCount: 0 };
    }

    const data = sessionDoc.data() as FirestoreTeacherSession;
    const currentCount = data.deviceCount || 0;
    const maxDevices = data.maxDevices || 30;

    // Check device limit (teachers don't count against limit)
    if (userType === 'student' && currentCount >= maxDevices) {
      return { success: false, deviceCount: currentCount };
    }

    // Add device to subcollection
    const deviceRef = doc(db, 'sessions', teacherId, 'devices', deviceId);
    await setDoc(deviceRef, {
      sessionId: teacherId,
      deviceId,
      userType,
      lastActive: serverTimestamp(),
      createdAt: serverTimestamp(),
    });

    // Increment device count (only for students)
    if (userType === 'student') {
      await updateDoc(sessionRef, {
        deviceCount: increment(1),
        updatedAt: serverTimestamp(),
      });
    }

    return { success: true, deviceCount: currentCount + (userType === 'student' ? 1 : 0) };
  },

  /**
   * Unregister a device.
   */
  async unregisterDevice(
    teacherId: string,
    deviceId: string
  ): Promise<void> {
    const deviceRef = doc(db, 'sessions', teacherId, 'devices', deviceId);
    const deviceDoc = await getDoc(deviceRef);

    if (deviceDoc.exists()) {
      const data = deviceDoc.data() as FirestoreDevice;

      // Delete the device
      await deleteDoc(deviceRef);

      // Decrement count if it was a student device
      if (data.userType === 'student') {
        const sessionRef = doc(db, 'sessions', teacherId);
        await updateDoc(sessionRef, {
          deviceCount: increment(-1),
          updatedAt: serverTimestamp(),
        });
      }
    }
  },

  /**
   * Clear all devices for a session.
   */
  async clearAllDevices(teacherId: string): Promise<void> {
    const devicesRef = collection(db, 'sessions', teacherId, 'devices');
    const devicesSnapshot = await getDocs(devicesRef);

    for (const deviceDoc of devicesSnapshot.docs) {
      await deleteDoc(deviceDoc.ref);
    }

    // Reset device count
    const sessionRef = doc(db, 'sessions', teacherId);
    const sessionDoc = await getDoc(sessionRef);
    if (sessionDoc.exists()) {
      await updateDoc(sessionRef, {
        deviceCount: 0,
        updatedAt: serverTimestamp(),
      });
    }
  },

  /**
   * Get current device count for a session.
   */
  async getDeviceCount(teacherId: string): Promise<number> {
    const sessionRef = doc(db, 'sessions', teacherId);
    const sessionDoc = await getDoc(sessionRef);

    if (!sessionDoc.exists()) {
      return 0;
    }

    return (sessionDoc.data() as FirestoreTeacherSession).deviceCount || 0;
  },

  /**
   * Subscribe to session changes (for real-time device count and cast updates).
   */
  subscribeToSession(
    teacherId: string,
    onUpdate: (data: {
      animalCode: AnimalType[];
      deviceCount: number;
      castAppId: string | null;
      castStartedAt: number | null;
    } | null) => void
  ): Unsubscribe {
    const sessionRef = doc(db, 'sessions', teacherId);

    return onSnapshot(sessionRef, (snapshot) => {
      if (!snapshot.exists()) {
        onUpdate(null);
        return;
      }

      const data = snapshot.data() as FirestoreTeacherSession;
      onUpdate({
        animalCode: data.animalCode,
        deviceCount: data.deviceCount || 0,
        castAppId: data.cast?.appId || null,
        castStartedAt: data.cast?.startedAt?.toMillis() || null,
      });
    });
  },

  /**
   * Start casting an app to students.
   * Only one app can be cast at a time - new cast replaces old.
   */
  async startCast(
    teacherId: string,
    appId: string,
    deviceId: string
  ): Promise<void> {
    const sessionRef = doc(db, 'sessions', teacherId);
    await updateDoc(sessionRef, {
      cast: {
        appId,
        startedAt: serverTimestamp(),
        teacherDeviceId: deviceId,
      },
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Stop casting (students see waiting screen).
   */
  async stopCast(teacherId: string): Promise<void> {
    const sessionRef = doc(db, 'sessions', teacherId);
    await updateDoc(sessionRef, {
      cast: null,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Find teacher ID by validating an animal code.
   * This allows students to log in without knowing the teacher ID.
   * Returns the teacher ID if a matching session is found.
   */
  async findTeacherByAnimalCode(code: AnimalType[]): Promise<string | null> {
    // Query all active sessions
    const sessionsRef = collection(db, 'sessions');
    const snapshot = await getDocs(sessionsRef);

    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;

    for (const sessionDoc of snapshot.docs) {
      const data = sessionDoc.data() as FirestoreTeacherSession;
      const lastLoginTime = data.lastLogin?.toMillis() || 0;

      // Skip expired sessions
      if (now - lastLoginTime >= twentyFourHours) {
        continue;
      }

      // Check if animal code matches
      if (
        data.animalCode &&
        data.animalCode.length === code.length &&
        data.animalCode.every((animal, index) => animal === code[index])
      ) {
        return sessionDoc.id; // teacherId
      }
    }

    return null;
  },
};
